import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Wind, ShieldCheck, MapPin, Users, Leaf, Sparkles } from "lucide-react";

const values = [
  { icon: Leaf, title: "Cleaner cities", body: "Every certified test we facilitate is one less polluting vehicle on Indian roads." },
  { icon: ShieldCheck, title: "Trust by verification", body: "Only RTO-approved centres go live. Every partner is manually reviewed before onboarding." },
  { icon: Users, title: "Built for everyone", body: "From a first-time scooter owner to a 500-truck fleet — one platform, every workflow." },
  { icon: MapPin, title: "Pan-India by design", body: "City-first SEO, local-language support on the roadmap, and partners in every metro." },
];

const timeline = [
  { year: "2024", title: "The problem hit us", body: "Two of our founders spent 4 hours in line for a routine PUC test in Bengaluru. That was the moment." },
  { year: "2025", title: "First 50 partners", body: "We launched a beta with 50 hand-picked centres across Bengaluru, Delhi, and Mumbai." },
  { year: "2026", title: "480+ centres, growing", body: "Today VayuCheck serves customers and commercial fleets across 26 states." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="relative overflow-hidden bg-mesh">
        <div className="absolute top-24 -left-16 w-72 h-72 rounded-full bg-primary/15 blur-3xl animate-drift" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">About VayuCheck</span>
            </div>
            <h1 data-testid="about-hero-h1" className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[1]">
              We&apos;re on a mission to make
              <br />
              <span className="bg-gradient-to-r from-primary via-cyan-500 to-emerald-500 bg-clip-text text-transparent gradient-animated">India&apos;s air cleaner —</span>
              <br />one PUC at a time.
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              VayuCheck is India&apos;s largest Pollution Under Control (PUC) testing aggregator. We connect vehicle owners, transporters and fleet operators with verified RTO-approved centres across the country.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="md:col-span-5 flex justify-center">
            <div className="relative">
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-primary/20 via-cyan-100 to-emerald-100 flex items-center justify-center animate-float-slow shadow-2xl shadow-primary/10">
                <Wind className="w-32 h-32 text-primary" />
              </div>
              <div className="absolute -top-4 right-8 w-16 h-16 rounded-full bg-white shadow-lg border border-border flex items-center justify-center animate-float"><Leaf className="w-8 h-8 text-emerald-500" /></div>
              <div className="absolute bottom-2 -left-6 w-14 h-14 rounded-full bg-white shadow-lg border border-border flex items-center justify-center animate-float-slow"><ShieldCheck className="w-7 h-7 text-primary" /></div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-3 gap-16">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">01 — Why</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">India adds a car every 2 seconds. Compliance shouldn&apos;t take 2 hours.</h2>
          </div>
          <div className="md:col-span-2 space-y-6 text-muted-foreground leading-relaxed text-base">
            <p>Under the Motor Vehicles Act, every fuel-powered vehicle in India must carry a valid Pollution Under Control certificate. It&apos;s the country&apos;s single largest compliance workflow — and yet, most drivers still queue at a stall on the roadside, cash in hand, hoping the operator&apos;s equipment is calibrated.</p>
            <p>We built VayuCheck to fix that. A single national marketplace of verified testing centres, transparent pricing, digital certificates, and automated renewal reminders — so that clean-air compliance becomes as easy as booking a cab.</p>
            <p>For transporters and fleet operators, the stakes are higher. A single expired certificate on a highway can mean a ₹10,000 fine and a stranded vehicle. Our commercial dashboard makes fleet-wide compliance a one-screen operation.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-mesh-soft">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">02 — Values</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-12 max-w-3xl">What we believe.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="values-grid">
            {values.map((v) => (
              <motion.div key={v.title} whileHover={{ y: -4 }} className="bg-white border border-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/5 transition-shadow duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6"><v.icon className="w-6 h-6 text-primary" /></div>
                <div className="font-display text-lg font-bold tracking-tight">{v.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">03 — Journey</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-12">From frustration to a national network.</h2>
          <div className="grid md:grid-cols-3 gap-8" data-testid="timeline">
            {timeline.map((t) => (
              <div key={t.year} className="border-l-2 border-primary pl-6">
                <div className="font-display text-4xl font-black text-primary">{t.year}</div>
                <div className="font-display text-lg font-bold tracking-tight mt-2">{t.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-mesh-soft">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-6">Ready to skip the queue?</h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto">Book your PUC test in under a minute — or list your centre and get bookings on autopilot.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/book"><Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-semibold shadow-lg shadow-primary/25">Book PUC test <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/register?role=partner"><Button size="lg" variant="outline" className="rounded-full border-border text-foreground hover:bg-secondary h-12 px-8">List your centre</Button></Link>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} VayuCheck</div>
        <div className="flex gap-6 flex-wrap">
          <Link to="/about" className="hover:text-foreground">About</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
